import React from 'react';
import { ArrowUp, X } from 'lucide-react';
import { FOCUS } from '../ds';
import { cn } from '../../lib/utils';

const DISCLAIMER = 'AI coaching is for information only. For health, nutrition or injury advice, consult a medical professional.';

/**
 * The one place the athlete types (Figma "Coach panel · web": the composer).
 * `emphasise` lifts the small-print disclaimer when the coach has just given
 * health-adjacent advice. `context` (v2's Ask why): the chip naming what the
 * message is about ("About: …"), with `onClearContext` to drop it. `chip`: a
 * fixed label for the chip instead (a human coach's "Direct message").
 * Without chat (`allowed` false) the composer is the one way back:
 * Subscribe to keep your coach.
 */
export default function Composer({ value, onChange, onSubmit, disabled, allowed, inputRef, placeholder, emphasise, wide, context = null, onClearContext, chip = null }) {
  return (
    <div className={cn('shrink-0 px-4 pb-3 pt-3', wide ? 'mx-auto w-full max-w-[808px] pb-6' : 'border-t border-border-subtle bg-bg-panel')}>
      {allowed && (context || chip) && (
        <div className="mb-2 flex">
          <span className="inline-flex max-w-full items-center gap-1 rounded-[13px] border border-border-default bg-bg-surface py-1 pl-2.5 pr-1 type-caption text-text-secondary">
            <span className={cn('truncate', !context && 'pr-1.5')}>{context ? `About: ${context.label}` : chip}</span>
            {context && (
              <button type="button" onClick={onClearContext} aria-label="Remove what you're asking about"
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-text-tertiary hover:bg-bg-raised hover:text-text-primary">
                <X size={12} />
              </button>
            )}
          </span>
        </div>
      )}
      {allowed ? (
        <form onSubmit={onSubmit} className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder || (context ? 'Ask why…' : 'Message your coach')}
            aria-label="Message your coach"
            disabled={disabled}
            maxLength={4000}
            className="h-12 min-w-0 flex-1 rounded-full border border-border-default bg-bg-surface px-4 type-body text-text-primary outline-none transition-colors placeholder:text-text-tertiary focus:border-accent-default disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={disabled || !value.trim()}
            aria-label="Send"
            className={cn('flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-full transition-colors', FOCUS,
              'bg-accent-default text-accent-on hover:bg-accent-hover disabled:bg-bg-raised disabled:text-text-secondary')}
          >
            <ArrowUp size={18} />
          </button>
        </form>
      ) : (
        <a href="/settings?tab=plan" className={cn('flex h-[52px] w-full items-center justify-center rounded-full bg-accent-default px-6 type-button text-accent-on transition-colors hover:bg-accent-hover', FOCUS)}>
          Subscribe to keep your coach
        </a>
      )}
      <p className={cn('m-0 mt-2 text-center text-[10.5px] leading-snug transition-colors', emphasise ? 'font-medium text-zone-threshold' : 'text-text-tertiary')}>
        {DISCLAIMER}
      </p>
    </div>
  );
}
